import dayjs from "dayjs"
import { LNotification } from "@lib/abpUtility"
import { notifySuccess } from "@lib/helper"
import { downloadFile } from "@lib/helperFile"
import { NotificationTemplateDetailModel } from "@models/notificationTemplate"
import { PagedResultDto } from "@services/dto/pagedResultDto"
import http, { ApiRequestConfig } from "@services/httpService"

export interface ImportErrorItem {
  message: string
  cell: string
  value: string
}

export interface ImportAccountingCodeSettingResult {
  errors: ImportErrorItem[]
  createdCount: number
  updatedCount: number
}

class SystemConfigurationService {
  // EMAIL SERVER
  public async getListEmailSenderProfile(params: any): Promise<any> {
    const res = await http.get(
      "api/services/app/SystemConfiguration/GetListEmailSenderProfile",
      {
        params,
      }
    )
    const { result } = res.data
    return result
  }
  public async getEmailSenderProfile(id): Promise<any> {
    const res = await http.get(
      "api/services/app/SystemConfiguration/GetEmailSenderProfile",
      {
        params: { id },
      }
    )
    const { result } = res.data
    return result
  }
  public async createOrUpdateEmailSenderProfile(body: any): Promise<any> {
    const res = await http.post(
      "api/services/app/SystemConfiguration/CreateOrUpdateEmailSenderProfile",
      body
    )
    notifySuccess(
      LNotification("SUCCESS"),
      LNotification("SAVING_SUCCESSFULLY")
    )
    return res.data.result
  }
  /**
   * BE luôn trả HTTP 200 kèm `{ isSuccess, message }` — lỗi SMTP bị bắt và gói vào
   * body chứ không ném exception. Nên KHÔNG báo thành công ở đây; caller phải đọc
   * `isSuccess` rồi mới quyết định notify.
   */
  public async verifySendMailSmtp(body: any): Promise<any> {
    const res = await http.post(
      "api/services/app/SystemConfiguration/VerifySendMailSmtp",
      body
    )
    return res.data.result
  }
  // SMS provider
  public async getListSmsSenderProfile(params: any): Promise<any> {
    const res = await http.get(
      "api/services/app/SystemConfiguration/GetListSmsSenderProfile",
      {
        params,
      }
    )
    const { result } = res.data
    return result
  }
  public async getSmsSenderProfile(id): Promise<any> {
    const res = await http.get(
      "api/services/app/SystemConfiguration/GetSmsSenderProfile",
      {
        params: { id },
      }
    )
    const { result } = res.data
    return result
  }
  public async createOrUpdateSmsSenderProfile(body: any): Promise<any> {
    const res = await http.post(
      "api/services/app/SystemConfiguration/CreateOrUpdateSmsSenderProfile",
      body
    )
    notifySuccess(
      LNotification("SUCCESS"),
      LNotification("SAVING_SUCCESSFULLY")
    )
    return res.data.result
  }

  // EMAIL SMS
  public async getAllTemplatesMap(params: any): Promise<PagedResultDto<any>> {
    const res = await http.get(
      "api/services/app/TemplateNotify/GetAllTemplatesMap",
      {
        params,
      }
    )
    const { result } = res.data
    return result
  }
  public async getGetTemplate(id): Promise<any> {
    const res = await http.get("api/services/app/TemplateNotify/GetTemplate", {
      params: { id },
    })
    const { result } = res.data
    return NotificationTemplateDetailModel.assign(result)
  }

  public async getListPrintingTemplate(params?: any): Promise<any> {
    const res = await http.get(
      "api/services/app/TemplateNotify/GetListPrintingTemplate",
      {
        params,
      }
    )
    const { result } = res.data
    return result
  }

  public async createOrUpdateNotificationTemplate(body: any): Promise<any> {
    const res = await http.post(
      "api/services/app/TemplateNotify/CreateOrUpdateNotificationTemplate",
      body
    )
    notifySuccess(
      LNotification("SUCCESS"),
      LNotification("SAVING_SUCCESSFULLY")
    )
    return res.data.result
  }
  public async GetParameters(params): Promise<any> {
    const res = await http.get(
      "api/services/app/TemplateNotify/GetParametersV2",
      {
        params,
      }
    )
    const { result } = res.data
    return result
  }

  /*
   * Gợi ý tag đã dùng ở các biểu mẫu còn hoạt động, dùng cho `FormTags` ở màn chi tiết biểu mẫu in.
   * @param moduleId lọc theo module; bỏ trống thì lấy tag của mọi module
   * @returns danh sách tag đã chuẩn hoá ở backend; luôn là mảng, không bao giờ ném lỗi
   */
  public async getTemplateTagSuggestions(moduleId?: number): Promise<string[]> {
    // Gợi ý tag chỉ là tiện ích gõ nhanh: lỗi mạng/quyền KHÔNG được chặn việc lưu biểu mẫu và cũng
    // không nên hiện toast, vì người dùng vẫn tự gõ tag mới được. Vì vậy chặn cả toast của
    // interceptor chung (`skipErrorNotification`) lẫn exception (catch trả mảng rỗng).
    const config: ApiRequestConfig = {
      params: { moduleId },
      skipErrorNotification: true,
    }

    try {
      const res = await http.get(
        "api/services/app/TemplateNotify/GetTagSuggestions",
        config
      )
      const result: unknown = res?.data?.result
      return Array.isArray(result) ? (result as string[]) : []
    } catch {
      return []
    }
  }

  public async getAllAccountingEntity(params: any): Promise<PagedResultDto<any>> {
    const res = await http.get("api/services/app/SystemConfiguration/GetAllAccountingEntity", { params })
    return res.data.result
  }

  public async updateAccountingEntity(body: any): Promise<any> {
    const res = await http.put("api/services/app/SystemConfiguration/UpdateAccountingEntitySetting", body)
    notifySuccess(LNotification("SUCCESS"), LNotification("SAVING_SUCCESSFULLY"))
    return res.data.result
  }

  /** Tải tệp Excel mẫu import cấu hình mã kế toán. Layout trùng file export nên sửa rồi import lại được. */
  public async downloadAccountingCodeSettingTemplate(): Promise<void> {
    const res = await http.get("api/Imports/GetAccountingCodeSettingTemplate", {
      responseType: "blob",
    })
    downloadFile(
      res.data,
      `Import-AccountingCodeSetting-Template-${dayjs().format("DDMMYYYYHHmmss")}.xlsx`
    )
  }

  /**
   * Import cấu hình mã kế toán. BE all-or-nothing: `errors` có phần tử thì KHÔNG dòng nào được ghi
   * (createdCount/updatedCount đều 0), nên caller phải kiểm tra `errors` trước khi báo thành công.
   */
  public async importAccountingCodeSetting(file: File): Promise<ImportAccountingCodeSettingResult> {
    const formData = new FormData()
    formData.append("file", file)
    const res = await http.post("api/Imports/ImportAccountingCodeSetting", formData, {
      headers: { "content-type": "multipart/form-data" },
    })
    return res.data.result
  }

  /*
  * @param projectId
  * @returns
  */
  public async getListTemplate(params: any): Promise<PagedResultDto<any>> {
    const res = await http.get("api/services/app/TemplateNotify/GetAllTemplateMapWithProject", { params })
    const { result } = res.data
    // If result is an array, wrap it in PagedResultDto structure
    if (Array.isArray(result)) {
      return {
        totalCount: result.length,
        items: result
      }
    }
    return result
  }

  /*
  * @param body
  * {
  *   "projectInput": 0,
  *   "projectOutput": 0,
  *   "lstNotificationCode": [ "string" ]
  * }
  * @returns
  */
  public async cloneTemplate(body: any): Promise<any> {
    const res = await http.post("api/services/app/TemplateNotify/CloneAllProjectTemplate", body)
    notifySuccess(LNotification("SUCCESS"), LNotification("CLONING_SUCCESSFULLY"))
    return res.data.result
  }

  public async getAllNumberingConfig(params: any): Promise<any> {
    const res = await http.get("api/services/app/NumberingConfig/GetAll", { params })
    return res.data.result
  }

  /*
  * @param body
  * {
  *   "id": 0, // nếu là update truyền vào
  *   "module": number, // module id
  *   "prefix": "string",
  *   "suffix": "string",
  *   "paddingZeroNumber": 6 //mặc định là 6, cho chọn từ 1-12
  * }
  * 
  */
  public async createOrUpdateNumberingConfig(body: any): Promise<any> {
    const res = await http.post("api/services/app/NumberingConfig/CreateOrUpdateNumberingConfig", body)
    notifySuccess(LNotification("SUCCESS"), LNotification("SAVING_SUCCESSFULLY"))
    return res.data.result
  }

  /**
   * Sửa thẳng số hiện tại của MỘT dòng bộ đếm (Feature-0371).
   *
   * `PUT` chứ không phải `POST`: backend đặt tên `UpdateLastNumberAsync`, ABP dynamic API suy verb
   * từ tiền tố `Update*`. Gọi bằng `http.post` sẽ nhận 404.
   *
   * Trả về `SequenceOutputDto` đầy đủ nên caller cập nhật thẳng dòng trong bảng được, không cần
   * gọi lại `GetAll`.
   */
  public async updateNumberingConfigLastNumber(body: {
    id: number
    lastNumber: number
  }): Promise<any> {
    const res = await http.put(
      "api/services/app/NumberingConfig/UpdateLastNumber",
      body
    )
    notifySuccess(LNotification("SUCCESS"), LNotification("SAVING_SUCCESSFULLY"))
    return res.data.result
  }

  public async getListModule(): Promise<any> {
    const res = await http.get("api/services/app/NumberingConfig/GetListModule")
    return res.data.result
  }

  public async sendMailSmtp(body: any): Promise<any> {
    const res = await http.post("api/services/app/SystemConfiguration/SendMailSmtp", body)
    notifySuccess(LNotification("SUCCESS"), LNotification("SEND_SUCCESSFULLY"))
    return res.data.result
  }
}

export default new SystemConfigurationService()
